import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { CATEGORIES, SIZE_LABELS } from "../data/artworks";

const STRIPE_CHECKOUT_API = process.env.REACT_APP_STRIPE_CHECKOUT_API;

const Arts = () => {
  const [artworks, setArtworks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedArtwork, setSelectedArtwork] = useState(null);
  const [selectedSize, setSelectedSize] = useState("small");
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [donateLoading, setDonateLoading] = useState(false);
  const [addedToCart, setAddedToCart] = useState(false);
  const { addToCart, cartItems } = useCart();

  // Fetch artworks from JSON
  useEffect(() => {
    fetch('/IMAGE/arts/artworks.json')
      .then(res => res.json())
      .then(data => {
        // Only show ready artworks
        setArtworks(data.filter(a => a.status === 'ready'));
        setLoading(false);
      })
      .catch(err => {
        console.error('Failed to load artworks:', err);
        setLoading(false);
      });
  }, []);

  const handleDonate = async (amount) => {
    setDonateLoading(true);
    try {
      const response = await fetch(STRIPE_CHECKOUT_API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{
            artwork_id: 'donate',
            title: 'Support / Donation',
            size: 'donate',
            size_label: 'Donation',
            price: amount,
            quantity: 1,
            image_url: window.location.origin + '/coffee.png',
          }],
          success_url: window.location.origin + '/checkout/success',
          cancel_url: window.location.href,
        }),
      });

      const data = await response.json();
      if (data.url) {
        window.location.href = data.url;
        // Don't reset loading - page will redirect
        return;
      }
    } catch (err) {
      console.error('Donate error:', err);
    }
    // Only reset if error or no redirect
    setDonateLoading(false);
  };

  const filteredArtworks = selectedCategory === "all"
    ? artworks
    : artworks.filter(art => art.tags?.includes(selectedCategory));

  const handleAddToCart = () => {
    if (selectedArtwork) {
      addToCart(selectedArtwork, selectedSize, selectedArtwork.prices[selectedSize]);
      setAddedToCart(true);
      setTimeout(() => setAddedToCart(false), 2000);
    }
  };

  const cartCount = cartItems.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <>
      <Helmet>
        <title>Gallery - limkc.com</title>
        <meta name="description" content="Fine art photography prints and digital downloads" />
      </Helmet>

      <div className="w-full h-full overflow-hidden">
        <style>{`
          /* Tablet (iPad Mini 744px - 1024px) - more bottom padding above footer */
          @media (min-width: 700px) and (max-width: 1024px) {
            .arts-page {
              padding-bottom: calc(var(--footer-height) + 6rem) !important;
            }
          }
        `}</style>
        <div
          className="arts-page px-4 py-12 pb-24 sm:pb-12 min-h-screen"
          style={{
            paddingTop: 'calc(var(--navbar-height) + 5rem)',
            backgroundColor: 'transparent',
            color: 'var(--text-primary)',
          }}
        >
          <div className="max-w-6xl mx-auto">
            {/* Header */}
            <div className="mb-12 text-center">
              <h1 className="text-4xl md:text-5xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
                Gallery
              </h1>
              <p className="text-lg max-w-2xl mx-auto" style={{ color: 'var(--text-secondary)' }}>
                
              </p>
            </div>

            {/* Category Filter - Horizontal scroll with arrows */}
            <div className="mb-12">
              <div className="relative flex items-center">
                {/* Left scroll button */}
                <button
                  onClick={() => {
                    const container = document.getElementById('categoryScroll');
                    container?.scrollBy({ left: -150, behavior: 'smooth' });
                  }}
                  className="absolute left-0 z-10 rounded-full flex items-center justify-center transition-opacity duration-200 opacity-0 pointer-events-none"
                  id="scrollLeftBtn"
                  style={{
                    width: '32px',
                    height: '32px',
                    backgroundColor: 'var(--bg-primary)',
                    boxShadow: '4px 0 12px rgba(0,0,0,0.2)',
                    border: '1px solid var(--bg-secondary)'
                  }}
                >
                  <svg className="w-4 h-4" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M10 12L6 8l4-4" />
                  </svg>
                </button>

                {/* Scrollable container */}
                <div
                  id="categoryScroll"
                  className="flex gap-2 overflow-x-auto justify-start sm:justify-center w-full"
                  style={{
                    scrollbarWidth: 'none',
                    msOverflowStyle: 'none',
                    scrollBehavior: 'smooth',
                    paddingLeft: '16px',
                    paddingRight: '16px',
                    marginLeft: '16px',
                    marginRight: '16px',
                    paddingTop: '4px',
                    paddingBottom: '4px'
                  }}
                  onScroll={(e) => {
                    const container = e.target;
                    const leftBtn = document.getElementById('scrollLeftBtn');
                    const rightBtn = document.getElementById('scrollRightBtn');
                    if (leftBtn) {
                      if (container.scrollLeft > 10) {
                        leftBtn.style.opacity = '1';
                        leftBtn.style.pointerEvents = 'auto';
                      } else {
                        leftBtn.style.opacity = '0';
                        leftBtn.style.pointerEvents = 'none';
                      }
                    }
                    if (rightBtn) {
                      if (container.scrollLeft < container.scrollWidth - container.clientWidth - 10) {
                        rightBtn.style.opacity = '1';
                        rightBtn.style.pointerEvents = 'auto';
                      } else {
                        rightBtn.style.opacity = '0';
                        rightBtn.style.pointerEvents = 'none';
                      }
                    }
                  }}
                >
                  <style>{`#categoryScroll::-webkit-scrollbar { display: none; }`}</style>
                  {CATEGORIES.map(cat => (
                    <button
                      key={cat.id}
                      onClick={() => setSelectedCategory(cat.id)}
                      className={`btn-3d btn-sm btn-pill flex-shrink-0 ${
                        selectedCategory === cat.id
                          ? 'btn-primary'
                          : ''
                      }`}
                      style={{ height: '32px' }}
                    >
                      {cat.label}
                    </button>
                  ))}
                </div>

                {/* Right scroll button */}
                <button
                  onClick={() => {
                    const container = document.getElementById('categoryScroll');
                    container?.scrollBy({ left: 150, behavior: 'smooth' });
                  }}
                  className="absolute right-0 z-10 rounded-full flex items-center justify-center transition-opacity duration-200 opacity-0 pointer-events-none"
                  id="scrollRightBtn"
                  style={{
                    width: '32px',
                    height: '32px',
                    backgroundColor: 'var(--bg-primary)',
                    boxShadow: '-4px 0 12px rgba(0,0,0,0.2)',
                    border: '1px solid var(--bg-secondary)'
                  }}
                >
                  <svg className="w-4 h-4" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M6 4l4 4-4 4" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Dynamic Gallery Grid */}
            {loading ? (
              <div className="flex justify-center items-center py-20">
                <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full"></div>
              </div>
            ) : filteredArtworks.length === 0 ? (
              <div className="text-center py-20" style={{ color: 'var(--text-secondary)' }}>
                No artworks found
              </div>
            ) : (() => {
              // Auto-detect orientation from resolution metadata
              const getOrientation = (artwork) => {
                if (artwork.orientation) return artwork.orientation;
                if (artwork.metadata?.resolution) {
                  const match = artwork.metadata.resolution.match(/(\d+)\s*[×x]\s*(\d+)/);
                  if (match) {
                    const width = parseInt(match[1]);
                    const height = parseInt(match[2]);
                    return width > height ? 'landscape' : 'portrait';
                  }
                }
                return 'portrait'; // default
              };

              // Build rows with randomized layout
              const rows = [];
              let i = 0;
              const arts = [...filteredArtworks];

              // Seed random based on artwork count for consistent layout
              const seededRandom = (seed) => {
                const x = Math.sin(seed) * 10000;
                return x - Math.floor(x);
              };

              // First row: find first landscape and show full width
              const firstLandscapeIdx = arts.findIndex(a => getOrientation(a) === 'landscape');
              if (firstLandscapeIdx !== -1) {
                const firstLandscape = arts[firstLandscapeIdx];
                arts.splice(firstLandscapeIdx, 1);
                rows.push({ type: 'full', items: [firstLandscape] });
              }

              // Remaining items: random arrangement
              while (i < arts.length) {
                const remaining = arts.length - i;
                const seed = i + arts.length + 7; // offset seed for variety
                const rand = seededRandom(seed);

                const current = arts[i];
                const isLandscape = getOrientation(current) === 'landscape';

                if (isLandscape) {
                  // Landscape: 100% or 66% + portrait (random)
                  if (rand < 0.3) {
                    // Full width
                    rows.push({ type: 'full', items: [current] });
                    i++;
                  } else {
                    // 2/3 landscape + find a portrait for 1/3
                    const portraitIdx = arts.slice(i + 1).findIndex(a => getOrientation(a) !== 'landscape');
                    if (portraitIdx !== -1) {
                      const portrait = arts[i + 1 + portraitIdx];
                      arts.splice(i + 1 + portraitIdx, 1);
                      if (rand < 0.65) {
                        rows.push({ type: 'two-third-left', items: [current, portrait] });
                      } else {
                        rows.push({ type: 'two-third-right', items: [portrait, current] });
                      }
                    } else {
                      rows.push({ type: 'full', items: [current] });
                    }
                    i++;
                  }
                } else if (!isLandscape && remaining >= 3) {
                  // Portrait: try to group 3 portraits in a row
                  const nextTwo = arts.slice(i + 1, i + 3).filter(a => getOrientation(a) !== 'landscape');
                  if (nextTwo.length === 2) {
                    rows.push({ type: 'thirds', items: [current, arts[i+1], arts[i+2]] });
                    i += 3;
                  } else {
                    // Find a landscape to pair with
                    const landscapeIdx = arts.slice(i + 1).findIndex(a => getOrientation(a) === 'landscape');
                    if (landscapeIdx !== -1) {
                      const landscape = arts[i + 1 + landscapeIdx];
                      arts.splice(i + 1 + landscapeIdx, 1);
                      if (rand < 0.5) {
                        rows.push({ type: 'two-third-right', items: [current, landscape] });
                      } else {
                        rows.push({ type: 'two-third-left', items: [landscape, current] });
                      }
                    } else {
                      rows.push({ type: 'single', items: [current] });
                    }
                    i++;
                  }
                } else {
                  // Fallback: single item
                  rows.push({ type: 'single', items: [current] });
                  i++;
                }
              }

              const renderArtworkCard = (artwork, className = '', useAspectRatio = false, colSpan = 1) => {
                // To get same height: landscape 4:3 in 2 cols, portrait needs 2:3 in 1 col
                // Math: 2 * (3/4) = 1.5 height units, so 1 * (3/2) = 1.5 height units
                const aspectRatio = useAspectRatio ? (colSpan === 2 ? '4/3' : '2/3') : 'auto';

                return (
                  <div
                    key={artwork.id}
                    className={`group cursor-pointer flex flex-col ${className}`}
                    onClick={() => setSelectedArtwork(artwork)}
                  >
                    <div
                      className="overflow-hidden mb-4"
                      style={{
                        backgroundColor: 'var(--bg-secondary)',
                        aspectRatio: aspectRatio
                      }}
                    >
                      <img
                        src={artwork.image}
                        alt={artwork.title}
                        className={`w-full h-full object-cover transition-transform duration-300 group-hover:scale-105`}
                        onError={(e) => {
                          e.target.src = `https://placehold.co/800x600/1a1a2e/ffffff?text=${encodeURIComponent(artwork.title)}`;
                        }}
                      />
                    </div>
                    <h3 className="font-medium text-lg" style={{ color: 'var(--text-primary)' }}>
                      {artwork.title}
                    </h3>
                    <p className="text-sm mb-1" style={{ color: 'var(--text-secondary)' }}>
                      {artwork.description}
                    </p>
                    <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                      From ${artwork.prices.digital}
                    </p>
                  </div>
                );
              };

              // Ornamental divider for mobile - Renaissance style
              const OrnamentDivider = () => (
                <div className="flex justify-center items-center py-4 sm:hidden">
                  <svg width="200" height="20" viewBox="0 0 200 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    {/* Left flourish - 3 peaks, amplitude: large→medium→small */}
                    <path
                      d="M5 10 Q15 8, 25 10 Q35 13, 45 10 Q55 5, 65 10 Q75 3, 85 10"
                      stroke="var(--text-secondary)"
                      strokeWidth="1"
                      fill="none"
                      opacity="0.35"
                    />
                    {/* Right flourish - mirrored 3 peaks */}
                    <path
                      d="M195 10 Q185 8, 175 10 Q165 13, 155 10 Q145 5, 135 10 Q125 3, 115 10"
                      stroke="var(--text-secondary)"
                      strokeWidth="1"
                      fill="none"
                      opacity="0.35"
                    />
                    {/* Center - 4 small diamonds forming larger diamond */}
                    {/* Top diamond */}
                    <path d="M100 5 L102 7 L100 9 L98 7 Z" fill="var(--text-secondary)" opacity="0.3"/>
                    {/* Left diamond */}
                    <path d="M96 8 L98 10 L96 12 L94 10 Z" fill="var(--text-secondary)" opacity="0.3"/>
                    {/* Right diamond */}
                    <path d="M104 8 L106 10 L104 12 L102 10 Z" fill="var(--text-secondary)" opacity="0.3"/>
                    {/* Bottom diamond */}
                    <path d="M100 11 L102 13 L100 15 L98 13 Z" fill="var(--text-secondary)" opacity="0.3"/>
                  </svg>
                </div>
              );

              return (
                <div className="space-y-4 sm:space-y-6 lg:space-y-8">
                  {rows.map((row, idx) => {
                    if (row.type === 'full') {
                      return (
                        <div key={idx}>
                          <OrnamentDivider />
                          {renderArtworkCard(row.items[0])}
                        </div>
                      );
                    }
                    if (row.type === 'two-third-left') {
                      return (
                        <div key={idx}>
                          {/* Desktop: side by side */}
                          <div className="hidden sm:block">
                            <OrnamentDivider />
                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
                              <div className="lg:col-span-2">{renderArtworkCard(row.items[0], '', true, 2)}</div>
                              <div>{renderArtworkCard(row.items[1], '', true, 1)}</div>
                            </div>
                          </div>
                          {/* Mobile: each artwork with divider */}
                          <div className="sm:hidden space-y-4">
                            <div><OrnamentDivider />{renderArtworkCard(row.items[0], '', true, 2)}</div>
                            <div><OrnamentDivider />{renderArtworkCard(row.items[1], '', true, 1)}</div>
                          </div>
                        </div>
                      );
                    }
                    if (row.type === 'two-third-right') {
                      return (
                        <div key={idx}>
                          {/* Desktop: side by side */}
                          <div className="hidden sm:block">
                            <OrnamentDivider />
                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
                              <div>{renderArtworkCard(row.items[0], '', true, 1)}</div>
                              <div className="lg:col-span-2">{renderArtworkCard(row.items[1], '', true, 2)}</div>
                            </div>
                          </div>
                          {/* Mobile: each artwork with divider */}
                          <div className="sm:hidden space-y-4">
                            <div><OrnamentDivider />{renderArtworkCard(row.items[0], '', true, 1)}</div>
                            <div><OrnamentDivider />{renderArtworkCard(row.items[1], '', true, 2)}</div>
                          </div>
                        </div>
                      );
                    }
                    if (row.type === 'thirds') {
                      return (
                        <div key={idx}>
                          {/* Desktop: 3 columns */}
                          <div className="hidden sm:block">
                            <OrnamentDivider />
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 lg:gap-8">
                              {row.items.map(art => renderArtworkCard(art, '', true, 1))}
                            </div>
                          </div>
                          {/* Mobile: each artwork with divider */}
                          <div className="sm:hidden space-y-4">
                            {row.items.map(art => (
                              <div key={art.id}><OrnamentDivider />{renderArtworkCard(art, '', true, 1)}</div>
                            ))}
                          </div>
                        </div>
                      );
                    }
                    // single
                    return (
                      <div key={idx}>
                        <OrnamentDivider />
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 lg:gap-8">
                          {renderArtworkCard(row.items[0])}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}

            {/* Incentives Section - hidden on mobile portrait */}
            <div className="hidden sm:block mt-24 pt-16 border-t" style={{ borderColor: 'var(--bg-secondary)' }}>
              <div className="grid grid-cols-1 gap-x-8 gap-y-10 lg:grid-cols-3">
                <div className="text-center">
                  <div className="btn-3d mx-auto w-10 h-10 flex items-center justify-center mb-4" style={{ padding: 0 }}>
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ position: 'relative', zIndex: 10, color: 'var(--text-primary)' }}>
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h3 className="text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>Pay Your Way</h3>
                  <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>Card, crypto, your choice. Secure checkout with instant confirmation.</p>
                </div>
                <div className="text-center">
                  <div className="btn-3d mx-auto w-10 h-10 flex items-center justify-center mb-4" style={{ padding: 0 }}>
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ position: 'relative', zIndex: 10, color: 'var(--text-primary)' }}>
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                    </svg>
                  </div>
                  <h3 className="text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>Museum Quality</h3>
                  <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>Giclée prints on archival Hahnemühle paper.</p>
                </div>
                <div className="text-center">
                  <div className="btn-3d mx-auto w-10 h-10 flex items-center justify-center mb-4" style={{ padding: 0 }}>
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ position: 'relative', zIndex: 10, color: 'var(--text-primary)' }}>
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h3 className="text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>Worldwide Shipping</h3>
                  <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>Carefully packaged and shipped to your door, anywhere on Earth.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Product Modal - Portal to body for true fixed positioning */}
      {selectedArtwork && createPortal(
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 pt-16 lg:pt-4"
          style={{ backgroundColor: 'rgba(0,0,0,0.8)' }}
          onClick={() => !donateLoading && setSelectedArtwork(null)}
        >
          {/* Loading overlay - outside blurred content */}
          {donateLoading && (
            <div className="absolute inset-0 z-[60] flex items-center justify-center">
              <div className="text-center">
                <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full mx-auto mb-2"></div>
                <p style={{ color: '#ffffff' }}>Redirecting to payment...</p>
              </div>
            </div>
          )}
          <div
            className="relative max-w-5xl w-full max-h-[90vh] overflow-y-auto rounded-xl p-6 lg:p-8"
            style={{
              backgroundColor: 'var(--bg-primary)',
              filter: donateLoading ? 'blur(4px)' : 'none',
              pointerEvents: donateLoading ? 'none' : 'auto',
              transition: 'filter 0.2s ease'
            }}
            onClick={e => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              onClick={() => setSelectedArtwork(null)}
              className="absolute top-4 right-4 p-2 rounded-full hover:opacity-75 transition z-10"
              style={{ backgroundColor: 'var(--bg-secondary)', color: 'var(--text-primary)' }}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            <div className="grid lg:grid-cols-2 gap-8 items-stretch">
              {/* Image with frame */}
              <div className="flex flex-col items-center justify-center">
                <div
                  className="relative cursor-pointer group flex items-center justify-center"
                  onClick={() => setIsFullscreen(true)}
                >
                  {/* Frame container - maintains aspect ratio */}
                  <div className="relative inline-block">
                    {/* Artwork image - positioned behind frame */}
                    <div
                      className="absolute flex items-center justify-center"
                      style={{
                        // Different padding for portrait vs landscape frames
                        ...(() => {
                          const isLandscape = (() => {
                            if (selectedArtwork.orientation === 'landscape') return true;
                            if (selectedArtwork.orientation === 'portrait') return false;
                            if (selectedArtwork.metadata?.resolution) {
                              const match = selectedArtwork.metadata.resolution.match(/(\d+)\s*[×x]\s*(\d+)/);
                              if (match) {
                                return parseInt(match[1]) > parseInt(match[2]);
                              }
                            }
                            return false;
                          })();
                          return isLandscape
                            ? { top: '19%', bottom: '19%', left: '17%', right: '17%' }
                            : { top: '0%', bottom: '0%', left: '14%', right: '14%' };
                        })(),
                        zIndex: 1
                      }}
                    >
                      <img
                        src={selectedArtwork.image}
                        alt={selectedArtwork.title}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.src = `https://placehold.co/800x600/1a1a2e/ffffff?text=${encodeURIComponent(selectedArtwork.title)}`;
                        }}
                      />
                    </div>
                    {/* Frame overlay - on top */}
                    <img
                      src={(() => {
                        // Detect orientation from metadata
                        if (selectedArtwork.orientation === 'portrait') return "/IMAGE/arts/pciframeportrait.png";
                        if (selectedArtwork.orientation === 'landscape') return "/IMAGE/arts/picframe.png";
                        // Auto-detect from resolution
                        if (selectedArtwork.metadata?.resolution) {
                          const match = selectedArtwork.metadata.resolution.match(/(\d+)\s*[×x]\s*(\d+)/);
                          if (match) {
                            const width = parseInt(match[1]);
                            const height = parseInt(match[2]);
                            return width > height ? "/IMAGE/arts/picframe.png" : "/IMAGE/arts/pciframeportrait.png";
                          }
                        }
                        return "/IMAGE/arts/pciframeportrait.png"; // default portrait
                      })()}
                      alt=""
                      className="relative w-full h-auto max-h-[60vh]"
                      style={{ zIndex: 2 }}
                    />
                  </div>
                  {/* Fullscreen icon */}
                  <div className="absolute bottom-3 right-3 p-2 rounded-md bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                    </svg>
                  </div>
                </div>
                {/* Frame disclaimer */}
                <p className="text-xs mt-3 italic text-center" style={{ color: 'var(--text-secondary)', opacity: 0.7 }}>
                  *Frame shown for illustration only, not included with print.
                </p>
              </div>

              {/* Details */}
              <div className="flex flex-col">
                <h2 className="text-2xl lg:text-3xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>
                  {selectedArtwork.title}
                </h2>
                <p className="text-sm mb-4" style={{ color: 'var(--text-secondary)' }}>
                  {selectedArtwork.edition}
                </p>
                <p className="mb-6" style={{ color: 'var(--text-secondary)' }}>
                  {selectedArtwork.description}
                </p>

                {/* Size Selection */}
                <div className="mb-6">
                  <h3 className="text-sm font-medium mb-3" style={{ color: 'var(--text-primary)' }}>
                    Select Option
                  </h3>
                  <div className="space-y-3">
                    {Object.entries(selectedArtwork.prices).map(([size, price]) => (
                      <button
                        key={size}
                        onClick={() => setSelectedSize(size)}
                        className={`w-full p-4 rounded-lg text-left transition-all flex justify-between items-center ${
                          selectedSize === size ? 'ring-2 ring-blue-600' : ''
                        }`}
                        style={{
                          backgroundColor: 'var(--bg-secondary)',
                          color: 'var(--text-primary)'
                        }}
                      >
                        <span className="font-medium">{SIZE_LABELS[size]}</span>
                        <span className="font-bold">${price}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Add to Cart */}
                <div className="mt-auto flex gap-3">
                  <button
                    onClick={handleAddToCart}
                    className="flex-1 py-3 px-6 rounded-lg bg-blue-600 font-medium hover:bg-blue-700 transition"
                    style={{ color: '#ffffff' }}
                  >
                    {addedToCart ? '✓ Added!' : `Add to Cart - $${selectedArtwork.prices[selectedSize]}`}
                  </button>
                  {cartCount > 0 && (
                    <Link
                      to="/cart"
                      className="py-3 px-4 rounded-lg flex items-center gap-2 hover:opacity-80 transition"
                      style={{ backgroundColor: 'var(--bg-secondary)', color: 'var(--text-primary)' }}
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                      <span className="font-medium">{cartCount}</span>
                    </Link>
                  )}
                </div>

                {/* Donate Option */}
                {!selectedArtwork.isDonation && (
                  <>
                    <div className="my-4 flex items-center gap-3">
                      <div className="flex-1 border-t" style={{ borderColor: 'var(--text-secondary)' }}></div>
                      <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>OR</span>
                      <div className="flex-1 border-t" style={{ borderColor: 'var(--text-secondary)' }}></div>
                    </div>
                    <div className="p-3 rounded-lg" style={{ backgroundColor: 'var(--bg-secondary)', border: '1px dashed var(--text-secondary)' }}>
                      <div className="flex flex-col sm:flex-row items-center justify-between gap-2">
                        <div className="flex items-center gap-2">
                          <span className="text-xs sm:text-sm" style={{ color: 'var(--text-secondary)' }}>Show some love</span>
                          <img src="/hand.png" alt="love" className="w-5 h-5 sm:w-7 sm:h-7 object-contain dark-invert" />
                          <img src="/coffee.png" alt="coffee" className="w-5 h-5 sm:w-7 sm:h-7 object-contain" />
                        </div>
                        <div className="flex items-center gap-1 sm:gap-2">
                          {donateLoading ? (
                            <span className="text-xs sm:text-sm px-2 py-1" style={{ color: 'var(--text-secondary)' }}>Redirecting...</span>
                          ) : (
                            <>
                              <button
                                onClick={() => handleDonate(0.50)}
                                className="text-xs sm:text-sm px-2 py-1 rounded-md bg-blue-600 hover:bg-blue-700 transition"
                                style={{ color: '#ffffff' }}
                              >
                                $0.50
                              </button>
                              <button
                                onClick={() => handleDonate(1)}
                                className="text-xs sm:text-sm px-2 py-1 rounded-md bg-blue-600 hover:bg-blue-700 transition"
                                style={{ color: '#ffffff' }}
                              >
                                $1
                              </button>
                              <button
                                onClick={() => handleDonate(5)}
                                className="text-xs sm:text-sm px-2 py-1 rounded-md bg-blue-600 hover:bg-blue-700 transition"
                                style={{ color: '#ffffff' }}
                              >
                                $5
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </>
                )}

                {/* Info */}
                <div className="mt-6 pt-6 border-t" style={{ borderColor: 'var(--bg-secondary)' }}>
                  <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                    {selectedSize === 'digital'
                      ? 'High-resolution file delivered instantly via email after purchase.'
                      : 'Museum-quality giclée print on archival paper. Ships worldwide.'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Fullscreen Image View - Portal to body for true fixed positioning */}
      {isFullscreen && selectedArtwork && createPortal(
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black"
          onClick={() => setIsFullscreen(false)}
        >
          {/* Close/Minimize button */}
          <button
            onClick={() => setIsFullscreen(false)}
            className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 transition z-10"
          >
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {/* Image */}
          <img
            src={selectedArtwork.image}
            alt={selectedArtwork.title}
            className="max-w-full max-h-full object-contain"
            onError={(e) => {
              e.target.src = `https://placehold.co/800x600/1a1a2e/ffffff?text=${encodeURIComponent(selectedArtwork.title)}`;
            }}
          />

          {/* Watermark - Repetitive Logo Pattern */}
          <div
            className="absolute inset-0 pointer-events-none overflow-hidden select-none"
            style={{ transform: 'rotate(-30deg) scale(1.5)' }}
          >
            <div
              className="absolute inset-0 grid"
              style={{
                gridTemplateColumns: 'repeat(auto-fill, 250px)',
                gridTemplateRows: 'repeat(auto-fill, 250px)',
                gap: '0px',
                opacity: 0.05,
              }}
            >
              {[...Array(50)].map((_, i) => (
                <div key={i} className="flex items-center justify-center">
                  <img src="/IMG_0204.PNG" alt="" className="w-12 h-12" />
                </div>
              ))}
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default Arts;
